import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './ReviewHeader.scss';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ProtocolIntelligence from '../protocolIntelligence/ProtocolIntelligence';
import ReviewAndApprove from '../review-and-approve/ReviewAndApprove';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ToastType} from '../../../../shared/constants';
import ApprovedProtocolActions from '../approved-protocol-actions/ApprovedProtocolActions';
import {
  useGetProtocolIntelligenceDetails,
  useGetAllProtocolIntelligenceList,
} from '../../../../hooks/ai/review/useReview';
import {
  ProtocolVersion,
  ProtocolDetailsResponse,
} from '../../../../api/types/aiReview.type';
import EditProtocolIntelligence from '../protocolIntelligence/editProtocolIntelligence/EditProtocolIntelligence';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';

const ManageProtocolStatusUpdated = 'ManageProtocolStatusUpdated';

export type ReviewHeaderToastType = {
  isOpen: boolean;
  message: string;
  status: ToastType;
};

type ReviewHeaderContextType = {
  isManageProtocolOpen: boolean;
  setIsManageProtocolOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleManageProtocolClose: () => void;
  handleShowReviewAndApproveOpen: () => void;
  handleApproveArtifacts: () => void;
  setReviewHeaderToast: React.Dispatch<
    React.SetStateAction<ReviewHeaderToastType>
  >;
  setToIsApprovalPendingState: () => void;
  hasValidationErrors: boolean;
  setHasValidationErrors: React.Dispatch<React.SetStateAction<boolean>>;
  isEditingProtocol: boolean;
  setIsEditingProtocol: React.Dispatch<React.SetStateAction<boolean>>;
  handlReviewHeaderToastClose: () => void;
  refetchProtocolData: () => void;
  refetchProtocolList: () => void;
  protocolDetails: ProtocolDetailsResponse | null;
  showEditProtocolIntelligence: boolean;
  setShowEditProtocolIntelligence: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  resetStates: () => void;
  protocolVersions: ProtocolVersion[];
  setSelectedProtocolId: React.Dispatch<React.SetStateAction<number | null>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  isProtocolUpdated: boolean;
  setIsProtocolUpdated: React.Dispatch<React.SetStateAction<boolean>>;
  clearSearchAndFetchProtocolList: () => void;
};

export type IEditProtocolChildRef = {
  handleEditProtocolClose: () => void;
};

export const ReviewHeaderContext =
  createContext<ReviewHeaderContextType | null>(null);

export const useReviewHeaderContext = () => {
  const ctx = useContext(ReviewHeaderContext);
  if (!ctx) {
    throw new Error('useReviewHeaderContext must be used within a Provider');
  }
  return ctx;
};

const reviewHeaderToastDefaultValue = {
  isOpen: false,
  message: '',
  status: ToastType.Success,
};

// ============================ReviewHeader Component==============================

type ReviewHeaderProps = {
  isManageProtocolOpen?: boolean;
  onManageProtocolOpenChange?: React.Dispatch<React.SetStateAction<boolean>>;
};

const ReviewHeader: React.FC<ReviewHeaderProps> = ({
  isManageProtocolOpen: isManageProtocolOpenProp,
  onManageProtocolOpenChange,
}) => {
  const [showReviewAndApprove, setShowReviewAndApprove] = useState(false);
  const [isManageProtocolOpenInternal, setIsManageProtocolOpenInternal] =
    useState(false);
  const isManageProtocolOpen =
    isManageProtocolOpenProp ?? isManageProtocolOpenInternal;
  const setIsManageProtocolOpen =
    onManageProtocolOpenChange ?? setIsManageProtocolOpenInternal;
  const [reviewHeaderToast, setReviewHeaderToast] =
    useState<ReviewHeaderToastType>(reviewHeaderToastDefaultValue);
  // This state helps to check for any validation errors
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [isEditingProtocol, setIsEditingProtocol] = useState(false);
  // This state helps to EDIT the Protocol Intelligence data
  const [showEditProtocolIntelligence, setShowEditProtocolIntelligence] =
    useState(false);
  const [isApiError, setIsApiError] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState<number | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isProtocolUpdated, setIsProtocolUpdated] = useState<boolean>(false);
  // ==========SignalR=================
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const editProtocolChildRef = useRef<IEditProtocolChildRef>(null);

  // Fetch ProtocolIds list
  const {
    data: {protocolVersions = []} = {},
    refetch: refetchProtocolList,
    error: protocolListError,
    isFetching: isFetchingProtocolList,
  } = useGetAllProtocolIntelligenceList(searchTerm, isManageProtocolOpen);

  const {
    data: protocolDetails = null,
    refetch: refetchProtocolData,
    error: protocolDetailsError,
    isFetching: isFetchingProtocolDetails,
  } = useGetProtocolIntelligenceDetails(
    isManageProtocolOpen ? selectedProtocolId : undefined
  );

  useEffect(() => {
    if (!isManageProtocolOpen) return;
    if (searchTerm === '') {
      refetchProtocolList();
    }
  }, [searchTerm, isManageProtocolOpen, refetchProtocolList]);

  useEffect(() => {
    if (
      selectedProtocolId &&
      (showReviewAndApprove || isEditingProtocol) &&
      !isFetchingProtocolDetails
    ) {
      refetchProtocolData();
    }
  }, [selectedProtocolId, showReviewAndApprove, isEditingProtocol]);

  useEffect(() => {
    const isError = !!protocolListError || !!protocolDetailsError;
    setIsApiError(isError);
  }, [protocolListError, protocolDetailsError]);

  const retryProtocolApis = async () => {
    try {
      if (protocolListError) {
        await refetchProtocolList();
      }
      if (protocolDetailsError && selectedProtocolId) {
        await refetchProtocolData();
      }
      setIsApiError(false);
    } catch (error) {
      console.error('Retry failed', error);
      setIsApiError(true);
    }
  };

  const onProtocolRefreshClick = () => {
    retryProtocolApis();
  };

  // Check the signalR message navigate accordingly
  const triggerSignalRNavigation = () => {
    const {payload = {}, type = ''} = lastEvent?.payload ?? {};
    if (type !== ManageProtocolStatusUpdated || !isManageProtocolOpen) return;
    if (
      protocolVersions
        .map(({protocolId}) => protocolId)
        .includes(payload.protocolId)
    )
      clearSearchAndFetchProtocolList();
  };

  useEffect(() => {
    triggerSignalRNavigation();
  }, [lastEvent]);

  const clearSearchAndFetchProtocolList = () => {
    setSearchTerm('');
    refetchProtocolList();
  };

  const resetStates = () => {
    setShowReviewAndApprove(false);
    setReviewHeaderToast(reviewHeaderToastDefaultValue);
    setIsEditingProtocol(false);
    setShowEditProtocolIntelligence(false);
    setIsProtocolUpdated(false);
    setSelectedProtocolId(null);
    setIsApiError(false);
    setSearchTerm('');
  };

  const handleManageProtocolClose = () => {
    if (isProtocolUpdated) {
      editProtocolChildRef.current?.handleEditProtocolClose();
      return;
    }
    setIsManageProtocolOpen(false);
    resetStates();
  };

  const handleShowReviewAndApproveOpen = () => {
    setShowReviewAndApprove(true);
  };

  const handleApproveArtifacts = () => {
    setShowReviewAndApprove(false);
    clearSearchAndFetchProtocolList();
  };

  const setToIsApprovalPendingState = () => {
    setShowReviewAndApprove(false);
    setIsProtocolUpdated(false);
    clearSearchAndFetchProtocolList();
  };

  const handlReviewHeaderToastClose = () => {
    setReviewHeaderToast(reviewHeaderToastDefaultValue);
  };

  const renderProtocolIntelligenceContent = () => {
    // Sequentially check the flags to decide which component to render in the drawer.
    // Please do not change the order of these checks as they are based on the functional flow of Protocol Intelligence.
    if (showEditProtocolIntelligence) {
      return (
        <EditProtocolIntelligence
          setIsEditingProtocol={setIsEditingProtocol}
          ref={editProtocolChildRef}
        />
      );
    }
    if (showReviewAndApprove) {
      return <ReviewAndApprove />;
    }
    if (isEditingProtocol) {
      return <ApprovedProtocolActions protocolId={selectedProtocolId} />;
    }
    return (
      <ProtocolIntelligence
        isApiError={isApiError}
        onProtocolRefreshClick={onProtocolRefreshClick}
      />
    );
  };

  const is_Protocol_Loading =
    isFetchingProtocolList || isFetchingProtocolDetails;

  // =======================Component Render=========================
  return (
    <Box className="review-header-container">
      <Typography
        className="typo-text subhead1_size"
        variant="h1"
        data-testid={`review-header-title`}
      >
        {t('ai.review.header.title')}
        <ArrTooltipWrapper
          trigger={
            <InfoIcon
              className="info-icon-label"
              data-testid="toggle-info-tooltip"
              role="button"
              tabIndex={0}
              aria-label={t('ai.review.header.infoTooltipLabel')}
            />
          }
          title={t('ai.review.header.title')}
        >
          <Box
            dangerouslySetInnerHTML={{
              __html: t('ai.review.header.titleTooltipText'),
            }}
            data-testid={`ai-review-header-title-tooltip`}
          ></Box>
        </ArrTooltipWrapper>
      </Typography>
      <ArrDrawer
        isOpen={isManageProtocolOpen || showReviewAndApprove}
        ariaLabel={t('ai.review.header.title')}
        onClose={handleManageProtocolClose}
        size="small"
        side="right"
        showButton={false}
        showHeader={false}
        className="manage-protocol-drawer"
      >
        <ReviewHeaderContext.Provider
          value={{
            isManageProtocolOpen,
            handleManageProtocolClose,
            handleShowReviewAndApproveOpen,
            handleApproveArtifacts,
            setReviewHeaderToast,
            setToIsApprovalPendingState,
            hasValidationErrors,
            setHasValidationErrors,
            isEditingProtocol,
            setIsEditingProtocol,
            handlReviewHeaderToastClose,
            refetchProtocolData,
            refetchProtocolList,
            protocolDetails,
            setShowEditProtocolIntelligence,
            showEditProtocolIntelligence,
            resetStates,
            protocolVersions,
            setSelectedProtocolId,
            searchTerm,
            setSearchTerm,
            isProtocolUpdated,
            setIsProtocolUpdated,
            setIsManageProtocolOpen,
            clearSearchAndFetchProtocolList,
          }}
        >
          <Box
            className="review-header-sr-only"
            aria-live="polite"
            aria-atomic="true"
            data-testid="toast-live-region"
          />
          {is_Protocol_Loading ? (
            <ArrLoadingSpinner
              isOpen={true}
              show={true}
              ariaLabel={t('common.loading')}
            />
          ) : (
            renderProtocolIntelligenceContent()
          )}
        </ReviewHeaderContext.Provider>
      </ArrDrawer>
      {reviewHeaderToast.isOpen && (
        <ArrToastNotification
          message={reviewHeaderToast.message}
          onClose={handlReviewHeaderToastClose}
          status={[reviewHeaderToast.status]}
          data-testid="toast-message"
          aria-label={reviewHeaderToast.message}
        />
      )}
    </Box>
  );
};

export default ReviewHeader;
